import type { ReactNode } from 'react';
import { cx } from '../cx';

/**
 * A NOTICE BAND IN THE CONTENT (redesign register L4, L5, D9, D10, rule R47; brief
 * FE-FOUNDATION section 7; components page section 9).
 *
 * The one place besides the tile row where L4 allows a fill, and it carries meaning:
 *  - `warning`: a source is stale. The late role as a tint (`--color-notice-warning`), the
 *    sentence in ink, one text action ("Retry now").
 *  - `danger`: the client page's alert (L5, used by the client page package). The act-now role
 *    as a tint (`--color-notice-danger`), the sentence bold in the act-now tone, the quote under
 *    it (the caller passes it through `ExpandableText`, two lines), the data age, and the page's
 *    one prominent button.
 *
 * PASSIVE, NEVER ON A TIMER (R47: say what failed, since when, what to do). `role="status"`, so
 * a screen reader hears it without being interrupted. An action the person cannot take is not
 * passed in and so is not drawn: hidden, never dimmed (components page section 9 "Disabled").
 */
export type InlineNoticeTone = 'warning' | 'danger';

export interface InlineNoticeProps {
  readonly tone: InlineNoticeTone;
  /** The sentence: what is wrong and since when. */
  readonly children: ReactNode;
  /** Under the sentence (danger): the quote, already clamped by the caller. */
  readonly quote?: ReactNode | undefined;
  /** The data age line (danger), e.g. "Telegram read 3 hours ago". */
  readonly age?: ReactNode | undefined;
  /** The one action: a text button (warning) or the page's prominent button (danger). */
  readonly action?: ReactNode | undefined;
  readonly className?: string | undefined;
}

export function InlineNotice({ tone, children, quote, age, action, className }: InlineNoticeProps) {
  return (
    <div
      role="status"
      data-inline-notice={tone}
      className={cx(
        'flex flex-wrap items-center gap-x-4 gap-y-2 rounded-(--record-group-radius) px-4 py-3',
        tone === 'warning' ? 'bg-(--color-notice-warning)' : 'bg-(--color-notice-danger)',
        className,
      )}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div
          className={cx(
            'text-sm',
            tone === 'warning' ? 'text-ink' : 'font-semibold text-(--color-status-act-now)',
          )}
        >
          {children}
        </div>
        {quote !== undefined && <div className="text-sm text-ink">{quote}</div>}
        {age !== undefined && <div className="text-2xs text-ink-muted">{age}</div>}
      </div>
      {action !== undefined && <div className="flex shrink-0 items-center">{action}</div>}
    </div>
  );
}
